import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Displays variant-specific metafield text and reacts to variant changes.
 * Uses pre-rendered JSON data (keyed by variant ID) when available,
 * falling back to querying the fetched HTML by block ID.
 */
class VariantMetafieldText extends HTMLElement {
  /** @type {Record<string, string> | null} */
  #variantTexts = null;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #getVariantTexts() {
    if (this.#variantTexts) return this.#variantTexts;
    const script = this.querySelector('script[data-variant-texts]');
    if (!script) return null;
    try {
      this.#variantTexts = JSON.parse(script.textContent);
    } catch {
      this.#variantTexts = null;
    }
    return this.#variantTexts;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    const variantTexts = this.#getVariantTexts();

    if (variantTexts && variantId && variantId in variantTexts) {
      const textEl = this.querySelector('.text-block');
      if (textEl) {
        const newText = variantTexts[variantId] ?? '';
        if (textEl.innerHTML !== newText) {
          textEl.innerHTML = newText;
        }
        return;
      }
    }

    const newVariantMetafieldText = event.detail.data.html.querySelector(
      '[data-block-id="' + this.dataset.blockId + '"]'
    );
    if (!newVariantMetafieldText) return;

    if (this.innerHTML !== newVariantMetafieldText.innerHTML) {
      this.replaceWith(newVariantMetafieldText);
    }
  };
}

if (!customElements.get('variant-metafield-text')) {
  customElements.define('variant-metafield-text', VariantMetafieldText);
}
